import format from 'date-fns/format';
import addMinutes from 'date-fns/addMinutes';
import subHours from 'date-fns/subHours';
import subDays from 'date-fns/subDays';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import isSameDay from 'date-fns/isSameDay';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';

import { maybePlural } from 'utils/string';
import { DAY_IN_YEAR_FORMAT, US_FULL_DATE, MIN_INTERVAL } from 'consts/datetime';

import type { Milliseconds, Seconds } from 'types/time';
import type { Page } from 'consts/pages';

type DatePartType = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

type DateParts = Record<DatePartType, number>;

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getConfiguredTimezone = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_TIMEZONE;
  }

  return localStorage.getItem('timezone') || DEFAULT_TIMEZONE;
};

const parseTimezoneOffset = (tzName: string): number => {
  if (tzName === 'GMT') {
    return 0;
  }

  const matches = tzName.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!matches) {
    return 0;
  }

  const [, sign, hours, minutes = '0'] = matches;
  const signMult = sign === '+' ? 1 : -1;
  return signMult * (Number(hours) * 60 + Number(minutes));
};

const getTimezoneOffsetInMinutes = (date: Date, timezone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
  return parseTimezoneOffset(offset);
};

const getDatePartsInTimezone = (date: Date, timezone: string): DateParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const result = parts.reduce(
    (acc, part) => {
      if (['year', 'month', 'day', 'hour', 'minute', 'second'].includes(part.type)) {
        acc[part.type as DatePartType] = Number(part.value);
      }
      return acc;
    },
    {
      year: 0,
      month: 0,
      day: 0,
      hour: 0,
      minute: 0,
      second: 0,
    } as DateParts,
  );

  // Some browsers return hour: 24 at midnight with hour12: false.
  // Normalize to 0 to prevent unexpected date arithmetic.
  if (result.hour === 24) {
    result.hour = 0;
  }

  return result;
};

export const zonedDateTimeToUtcDate = (parts: DateParts, timezone = DEFAULT_TIMEZONE): Date => {
  let ts = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

  for (let i = 0; i < 3; i += 1) {
    const offsetMinutes = getTimezoneOffsetInMinutes(new Date(ts), timezone);
    const nextTs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const withOffset = nextTs - offsetMinutes * 60 * 1000;

    if (withOffset === ts) {
      break;
    }
    ts = withOffset;
  }

  return new Date(ts);
};

export const utcDateToZonedDate = (date: Date | number, timezone = getConfiguredTimezone()): Date => {
  const parts = getDatePartsInTimezone(new Date(date), timezone);
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
};

export const formatInTimezone = (
  date: Date | number | string,
  f: string,
  timezone = getConfiguredTimezone(),
): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(utcDateToZonedDate(d, timezone), f);
};

/**
 * Timezone-aware isSameDay comparison. Converts both dates to the configured
 * (or provided) timezone before comparing calendar day.
 */
export const isSameDayInTimezone = (
  dateA: Date | number,
  dateB: Date | number,
  timezone = getConfiguredTimezone(),
): boolean => {
  return isSameDay(utcDateToZonedDate(dateA, timezone), utcDateToZonedDate(dateB, timezone));
};

/**
 * timezone-aware isToday check.
 * returns true if the given date falls on
 * "today" in the configured (or provided) timezone.
 */
export const isTodayInTimezone = (date: Date | number, timezone = getConfiguredTimezone()): boolean => {
  return isSameDayInTimezone(date, new Date(), timezone);
};

/**
 * Timezone-aware isYesterday check. Returns true if the given date falls on
 * "yesterday" in the configured (or provided) timezone.
 */
export const isYesterdayInTimezone = (date: Date | number, timezone = getConfiguredTimezone()): boolean => {
  return isSameDayInTimezone(date, subDays(new Date(), 1), timezone);
};

/**
 * Timezone-aware isSameMonth comparison. Converts both dates to the configured
 * (or provided) timezone before comparing year and month.
 */
export const isSameMonthInTimezone = (
  dateA: Date | number,
  dateB: Date | number,
  timezone = getConfiguredTimezone(),
): boolean => {
  const partsA = getDatePartsInTimezone(new Date(dateA), timezone);
  const partsB = getDatePartsInTimezone(new Date(dateB), timezone);
  return partsA.year === partsB.year && partsA.month === partsB.month;
};

/**
 * Timezone-aware ISO 8601 formatting. Produces an ISO string representing the
 * wall-clock time in the configured (or provided) timezone, including the
 * correct UTC offset (e.g. "2025-02-05T10:30:00-05:00").
 */
export const formatISOInTimezone = (date: Date | number, timezone = getConfiguredTimezone()): string => {
  const d = new Date(date);
  const parts = getDatePartsInTimezone(d, timezone);
  const offsetMinutes = getTimezoneOffsetInMinutes(d, timezone);

  const pad = (n: number) => n.toString().padStart(2, '0');
  const datePart = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  const timePart = `${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;

  const absOffset = Math.abs(offsetMinutes);
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';
  const offsetHours = Math.floor(absOffset / 60);
  const offsetMins = absOffset % 60;
  const offsetPart = `${offsetSign}${pad(offsetHours)}:${pad(offsetMins)}`;

  return `${datePart}T${timePart}${offsetPart}`;
};

/**
 * Start of `date`'s calendar day in the given timezone, returned as a UTC
 * instant. Use anywhere a chart bucket, range label, or header needs to anchor
 * on midnight in the user-configured timezone instead of the browser's.
 */
export const getZonedDayStart = (date: Date, timezone = getConfiguredTimezone()): Date => {
  const parts = getDatePartsInTimezone(date, timezone);
  return zonedDateTimeToUtcDate({ ...parts, hour: 0, minute: 0, second: 0 }, timezone);
};

/**
 * Start of (today − `daysAgo`) calendar day in the given timezone, as a UTC
 * instant. Use for building day-bucket arrays in chart pages.
 */
export const getZonedDayStartForOffset = (daysAgo: number, timezone = getConfiguredTimezone()): Date => {
  const todayParts = getDatePartsInTimezone(new Date(), timezone);
  // UTC math handles month/year rollover when subtracting days.
  const offsetDay = new Date(Date.UTC(todayParts.year, todayParts.month - 1, todayParts.day - daysAgo));
  return zonedDateTimeToUtcDate(
    {
      year: offsetDay.getUTCFullYear(),
      month: offsetDay.getUTCMonth() + 1,
      day: offsetDay.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    timezone,
  );
};

/**
 * Parse a `YYYY-MM-DD` calendar-day string as start-of-day in the given
 * timezone. Use for backend day-keyed responses so timestamps align with
 * timezone-aware chart axes.
 */
export const parseZonedDayStart = (yyyyMmDd: string, timezone = getConfiguredTimezone()): Date => {
  const [year, month, day] = yyyyMmDd.split('-').map(Number);
  return zonedDateTimeToUtcDate({ year, month, day, hour: 0, minute: 0, second: 0 }, timezone);
};

/**
 * Inclusive list of `YYYY-MM-DD` day keys between `startDate` and `endDate` in
 * the given timezone, in chronological order. Use to "fill the gaps" in chart
 * data series. DST-safe — iterates via UTC day-arithmetic on the zoned date
 * parts, so it does not skip or duplicate buckets across spring-forward /
 * fall-back boundaries.
 */
export const getZonedDayKeyRange = (startDate: Date, endDate: Date, timezone = getConfiguredTimezone()): string[] => {
  const startParts = getDatePartsInTimezone(startDate, timezone);
  const endParts = getDatePartsInTimezone(endDate, timezone);
  const startMs = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
  const endMs = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
  const dayCount = Math.round((endMs - startMs) / 86_400_000) + 1;
  if (dayCount <= 0) return [];
  const pad = (n: number) => n.toString().padStart(2, '0');
  return Array.from({ length: dayCount }, (_, i) => {
    const d = new Date(Date.UTC(startParts.year, startParts.month - 1, startParts.day + i));
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  });
};

const getZonedDayEnd = (date: Date, timezone: string): Date => {
  const parts = getDatePartsInTimezone(date, timezone);
  const nextDayUtc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1, 0, 0, 0));
  const nextStart = zonedDateTimeToUtcDate(
    {
      year: nextDayUtc.getUTCFullYear(),
      month: nextDayUtc.getUTCMonth() + 1,
      day: nextDayUtc.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    timezone,
  );
  return new Date(nextStart.getTime() - 1000);
};

export const formatUTC = (date: Date, f: string): string => {
  return format(addMinutes(date, date.getTimezoneOffset()), f);
};

export const formatRechart = (date: Date | number, f: string): string => {
  try {
    return formatInTimezone(date, f);
  } catch {
    return '';
  }
};

type TimeRange = {
  t0: Seconds | null;
  t1: Seconds | null;
};

export const qRangeToTs = (qString: string, timezone = getConfiguredTimezone()): TimeRange => {
  if (!qString) {
    return { t0: null, t1: null };
  }
  const now = new Date();
  const nowSeconds: Seconds = Math.floor(now.getTime() / 1000) as Seconds;
  let t1: Seconds = nowSeconds;
  let t0: Seconds | null = null;

  switch (qString.toLowerCase()) {
    case 'today':
      t0 = Math.floor(getZonedDayStart(now, timezone).getTime() / 1000) as Seconds;
      break;
    case 'yesterday':
      t1 = Math.floor(getZonedDayEnd(subDays(now, 1), timezone).getTime() / 1000) as Seconds;
      t0 = Math.floor(getZonedDayStart(subDays(now, 1), timezone).getTime() / 1000) as Seconds;
      break;
    case 'last 2 hours':
      t0 = Math.floor(subHours(now, 2).getTime() / 1000) as Seconds;
      break;
    case 'last 12 hours':
      t0 = Math.floor(subHours(now, 12).getTime() / 1000) as Seconds;
      break;
    case 'last 24 hours':
      t0 = Math.floor(subHours(now, 24).getTime() / 1000) as Seconds;
      break;
    case 'last 7 days':
      t0 = Math.floor(subDays(now, 7).getTime() / 1000) as Seconds;
      break;
    case 'last 30 days':
      t0 = Math.floor(subDays(now, 30).getTime() / 1000) as Seconds;
      break;
    case 'last 90 days':
      t0 = Math.floor(subDays(now, 90).getTime() / 1000) as Seconds;
      break;
    case 'last hour':
      t0 = (nowSeconds - 3600) as Seconds;
      break;
    default:
      t0 = (nowSeconds - 3600) as Seconds;
  }

  return {
    t0: t0 !== null ? ((Math.floor(t0 / 60) * 60) as Seconds) : null,
    t1: t1 !== null ? ((Math.floor(t1 / 60) * 60) as Seconds) : null,
  };
};

export const dateToEpoch = (date: Date) => {
  return Math.floor(date.getTime() / 1000) as Seconds;
};
export const roundDownToNearestMinute = (epochTs: Seconds) => {
  return (epochTs - (epochTs % 60)) as Seconds;
};

type AdjustedInterval = {
  roundedFrom: Seconds;
  roundedTo: Seconds;
};

// adjust from and to to pick a range in 5 minute intervals
export const adjustTo5MinuteIntervals = (fromEpoch: Seconds, toEpoch: Seconds): AdjustedInterval => {
  const interval = 300;
  const now = Math.floor(new Date().getTime() / 1000);

  const testFromIntervals = Math.floor(fromEpoch / interval);
  const testToIntervals = Math.floor(toEpoch / interval);

  let testFrom = testFromIntervals * interval;
  let testTo = testToIntervals * interval;

  if (testTo - testFrom < interval) {
    if (testTo > now) {
      // If the 'to' time is in the future, adjust both 'from' and 'to' back one interval
      testTo -= interval;
      testFrom -= interval;
    } else {
      // If the range is less than one interval, extend 'to' to the next interval
      testTo += interval;
    }
  }

  return {
    roundedFrom: testFrom as Seconds,
    roundedTo: testTo as Seconds,
  };
};

type TimeDifference = {
  weeks: number;
  days: number;
  hours: number;
  minutes: number;
};

export const getDifference = (tBase: Date | Milliseconds, t1: Date | Milliseconds): TimeDifference => {
  const tBaseDate = tBase instanceof Date ? tBase : new Date(tBase);
  const t1Date = t1 instanceof Date ? t1 : new Date(t1);

  const weeks = differenceInWeeks(t1Date, tBaseDate);
  const days = differenceInDays(t1Date, tBaseDate);
  const hours = differenceInHours(t1Date, tBaseDate);
  const minutes = differenceInMinutes(t1Date, tBaseDate);

  return {
    weeks,
    days,
    hours,
    minutes,
  };
};

export const _maybePrefix = (value: number): string | number => (value > 0 ? `+${value}` : value);

export const _getString = (value: number, label: string): string =>
  `${_maybePrefix(value)} ${maybePlural(value, label)}`;

export const getDifferenceLoose = (tBase: Date | Milliseconds, t1: Date | Milliseconds): string | undefined => {
  const diff = getDifference(tBase, t1);
  if (diff.weeks) {
    return _getString(diff.weeks, 'week');
  }
  if (diff.days) {
    return _getString(diff.days, 'day');
  }
  if (diff.hours) {
    return _getString(diff.hours, 'hour');
  }
  if (diff.minutes) {
    return _getString(diff.minutes, 'minute');
  }
  return undefined;
};

const ALLOWED_INTERVALS: Milliseconds[] = [
  (60 * 1000) as Milliseconds, // minute
  (2 * 60 * 1000) as Milliseconds,
  (5 * 60 * 1000) as Milliseconds,
  (10 * 60 * 1000) as Milliseconds,
  (15 * 60 * 1000) as Milliseconds,
  (20 * 60 * 1000) as Milliseconds,
  (30 * 60 * 1000) as Milliseconds,
  (60 * 60 * 1000) as Milliseconds, // hour
  (2 * 60 * 60 * 1000) as Milliseconds,
  (4 * 60 * 60 * 1000) as Milliseconds,
  (12 * 60 * 60 * 1000) as Milliseconds,
  (24 * 60 * 60 * 1000) as Milliseconds, // day
  (7 * 24 * 60 * 60 * 1000) as Milliseconds, // week
  (14 * 24 * 60 * 60 * 1000) as Milliseconds, // 2 weeks
  (30 * 24 * 60 * 60 * 1000) as Milliseconds, // 30 days
];

export const getChartInterval = (
  minInterval: Milliseconds,
  diff: Milliseconds,
  targetNumberOfBuckets: number = 50,
): Milliseconds => {
  return (
    ALLOWED_INTERVALS.filter((a) => a >= minInterval)
      .sort((a, b) => b - a)
      .find((interval) => {
        const div = diff / interval;
        if (div > 1 && div >= targetNumberOfBuckets) {
          return true;
        }
        return false;
      }) || minInterval // fallback to minimum
  );
};

export const formatTimerange = (
  query: string,
  t0: Seconds,
  t1: Seconds,
  timezone = getConfiguredTimezone(),
): string => {
  if (query) {
    const lowerCaseQ = query.toLowerCase();
    return ['today', 'yesterday'].includes(lowerCaseQ) ? lowerCaseQ : `in the ${lowerCaseQ}`;
  }

  const t0Millis = (t0 * 1000) as Milliseconds;
  const t1Millis = (t1 * 1000) as Milliseconds;
  const t0Date = utcDateToZonedDate(t0Millis, timezone);
  const t1Date = utcDateToZonedDate(t1Millis, timezone);

  if (
    // if its a full date, i.e. 02/02/2021 from 00:00:00 to 23:59:59
    // Compare zoned dates against zoned start/end-of-day (not UTC millis
    // against machine-local millis, which breaks when machine tz != configured tz).
    t0Date.getTime() === startOfDay(t0Date).getTime() &&
    t1Date.getTime() === endOfDay(t1Date).getTime() - 999 &&
    isSameDay(t0Date, t1Date)
  ) {
    return `on ${formatInTimezone(t0Millis, DAY_IN_YEAR_FORMAT, timezone)}`;
  } else {
    // if its a time range, i.e. 02/02/2021 00:00:00 to 02/02/2021 23:59:59
    return `between ${formatInTimezone(t0Millis, US_FULL_DATE, timezone)} and ${formatInTimezone(t1Millis, US_FULL_DATE, timezone)}`;
  }
};

export const decideChartPointInterval = (page: Page, diff: Milliseconds): number | null => {
  // early exit if there's no point in charting
  if (!diff || !page) {
    return null;
  }

  return getChartInterval(MIN_INTERVAL[page] || MIN_INTERVAL.default, diff);
};

export const formatTimezoneOffset = (offsetInMinutes: number): string => {
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const minutes = Math.abs(offsetInMinutes) % 60;
  const sign = offsetInMinutes >= 0 ? '+' : '-';
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
